// A material sold in packs (Spencer, 2026-10-17): the price is what the vendor charges, as on the receipt, and the
// pack size is how many of what the recipe lines count it holds (a pack of 10 pulls at $39.59). A line counts pieces;
// each piece costs the price over the pack size. A material that "charges whole packs to the job" (a pull, rarely used
// twice) has the job's pieces rounded up to whole packs once for the job and the packs spread over the lines that use
// them: 11 pulls in packs of 10 cost 2 packs. One that does not (a hinge used on every job: the rest go to stock)
// charges only the pieces: 11 hinges cost 11 × $3.959.

import { Rat } from './rational.ts';
import type { LibMaterial } from './types.ts';

export interface MaterialPack {
  /** How many pieces the price buys. */
  size: Rat;
  /** What the vendor charges for the pack. */
  price: Rat;
  /** One piece: the price over the pack size, exact. */
  each: Rat;
  /** True when the job pays for whole packs. */
  whole: boolean;
}

/** The pack a material is sold in, or null when it is priced per piece (no pack size, or a pack of one that is not charged whole). */
export function materialPack(m: Pick<LibMaterial, 'price' | 'pack_size' | 'whole_packs'> | null | undefined): MaterialPack | null {
  if (!m) return null;
  const size = m.pack_size === null || m.pack_size === undefined || String(m.pack_size).trim() === '' ? null : Rat.tryParse(m.pack_size);
  if (!size || size.sign() <= 0) return null;
  const whole = !!m.whole_packs;
  if (size.eq(Rat.ONE) && !whole) return null;
  const price = m.price === null || m.price === undefined || String(m.price).trim() === '' ? Rat.ZERO : Rat.parse(m.price);
  return { size, price, each: price.div(size), whole };
}

/** How many whole packs a number of pieces takes. */
export function packsFor(pieces: Rat, size: Rat): Rat {
  if (pieces.sign() <= 0) return Rat.ZERO;
  return Rat.of(pieces.div(size).ceil());
}
